'use client';

// module imports
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// file imports
import { DeleteConfirmationModal } from '@/components';
import { deleteEvent, getEvents } from '@/redux/slices/eventSlice';
import type { AppDispatch } from '@/redux/store';

interface EventActionsProps {
  eventSlug: string;
  isOpen: boolean;
  onToggle: () => void;
  responses?: number;
}

export default function EventActions({ eventSlug, isOpen, onToggle, responses = 0 }: EventActionsProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'right' | 'left'>('right');
  const [verticalPosition, setVerticalPosition] = useState<'below' | 'above'>('below');
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await dispatch(deleteEvent(eventSlug));
      dispatch(getEvents({ params: { limit: 10, page: 1, sort: -1 } }));
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Failed to delete event:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  // Calculate dropdown position to prevent overflow and get button position
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      // const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      // const dropdownWidth = 160; // w-40 = 160px
      const dropdownHeight = 120; // Approximate height of dropdown with 3 items

      // Store button position for fixed positioning
      setButtonRect(rect);

      // Check if dropdown would overflow on the right side
      //  if (rect.right + dropdownWidth > viewportWidth) {
      //   setDropdownPosition('left');
      // } else {
      //   setDropdownPosition('right');
      // }

      // Always position on the right side
      setDropdownPosition('right');

      // Check if dropdown would overflow at the bottom
      if (rect.bottom + dropdownHeight > viewportHeight) {
        setVerticalPosition('above');
      } else {
        setVerticalPosition('below');
      }
    } else {
      setButtonRect(null);
    }
  }, [isOpen]);

  return (
    <div className="relative dropdown-container" ref={dropdownRef}>
      <button
        className="text-gray-500 hover:text-gray-800 cursor-pointer transition-colors duration-150 p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-label="Event actions menu"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {isOpen && buttonRect && (
        <div
          className="fixed w-40 bg-white rounded-lg shadow-xl z-50 border border-gray-200 dropdown-container"
          style={{
            top: verticalPosition === 'below' ? buttonRect.bottom + 8 : buttonRect.top - 128, // 120px dropdown height + 8px gap
            left: dropdownPosition === 'right' ? buttonRect.right - 160 : buttonRect.left,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1">
            {responses > 0 ? (
              <Link href={`/events/${eventSlug}/report`} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 whitespace-nowrap">
                <span className="mr-3 text-base">📊</span>View Report
              </Link>
            ) : (
              <div className="flex items-center px-4 py-2 text-sm text-gray-400 cursor-not-allowed whitespace-nowrap relative group" title="No responses available. Reports are generated after receiving feedback.">
                <span className="mr-3 text-base">📊</span>View Report
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">No responses available. Reports are generated after receiving feedback.</div>
              </div>
            )}
            <Link href={`/events/${eventSlug}/edit`} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 whitespace-nowrap">
              <span className="mr-3 text-base">✏️</span>Edit
            </Link>
            <button onClick={handleDeleteClick} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer transition-colors duration-150 whitespace-nowrap">
              <span className="mr-3 text-base">🗑️</span>Delete
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal isOpen={showDeleteModal} onClose={handleDeleteCancel} onConfirm={handleDeleteConfirm} title="Delete Event" message="Are you sure you want to delete this event? This action cannot be undone." confirmText="DELETE" isDeleting={isDeleting} itemName="event" />
    </div>
  );
}
